import { useEffect, useLayoutEffect, useRef, type PropsWithChildren } from 'react';
import type { PortalApplication, PortalIdentity } from '../model/portalModels';
import { ensureAccessToken } from '../modules/auth/accessTokenManager';
import { PortalFormSessionManager } from '../runtime/formSessionManager';
import { PortalFormSessionContext } from '../runtime/formSessionContext';
import { destroyRegisteredFormApplication, startRegisteredFormApplication, type PortalOperationFeedback } from '../runtime/wujieLoader';
import { usePortalOverlay } from './PortalOverlayProvider';

export function useAuthenticatedFormSessionManager(props: {
  identity?: PortalIdentity; applications: readonly PortalApplication[];
  navigate: (path: string) => void; onSessionExpired: () => void;
  reportOperationFeedback: (feedback: PortalOperationFeedback) => void;
}) {
  const callbacks = useRef(props); callbacks.current = props;
  const managerRef = useRef<PortalFormSessionManager | undefined>(undefined);
  if (!managerRef.current) managerRef.current = new PortalFormSessionManager({
    accessToken: (applicationCode, signal) => ensureAccessToken(applicationCode, false, signal),
    createFactory: (identity, token) => ({
      start: (record, application) => startRegisteredFormApplication({
        application, identity, runtimeName: record.runtimeName, formSessionHost: record.capability,
        signal: record.physicalAbort.signal, container: record.container,
        pathname: `${application.entry.basePath}${application.entry.defaultRoute}`,
        accessToken: token,
        refreshAccessToken: () => ensureAccessToken(application.appCode, true, record.physicalAbort.signal),
        registerPrimaryNavigation: () => undefined,
        onPrimaryNavigationChange: () => undefined,
        navigate: path => callbacks.current.navigate(`${application.entry.basePath}${path}`),
        onSessionExpired: () => callbacks.current.onSessionExpired(),
        reportOperationFeedback: feedback => callbacks.current.reportOperationFeedback(feedback),
      }),
      destroy: record => destroyRegisteredFormApplication(record.runtimeName),
    }),
  });
  const manager = managerRef.current;
  useLayoutEffect(() => {
    manager.setAuthenticatedContext(props.identity, props.applications);
  }, [manager, props.identity, props.applications]);
  useLayoutEffect(() => () => manager.setAuthenticatedContext(undefined), [manager]);
  return manager;
}

export function PortalFormSessionProvider(props: PropsWithChildren<{ manager: PortalFormSessionManager }>) {
  const { promptFormLeave } = usePortalOverlay();
  useLayoutEffect(() => {
    props.manager.setLeavePrompt(promptFormLeave);
    return () => props.manager.setLeavePrompt(undefined);
  }, [props.manager, promptFormLeave]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!props.manager.shouldBlockBeforeUnload()) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [props.manager]);
  return <PortalFormSessionContext.Provider value={props.manager}>{props.children}</PortalFormSessionContext.Provider>;
}
